import type { WegicRequestConfig } from '@wegic/request';
import type { InviteCodeRes, LoginParams, LoginRes, RegisterParams, RegisterRes, ResetParams, User } from '@/types';
import md5 from 'md5';
import { userRequest } from './request';
import { getRegisterTrackingParams } from './tracking';

const USER_API_BASE_URL = '/api/user/v1';

/**
 * 密码加密
 */
function encryptPassword(password: string): string {
  return md5(password);
}

/**
 * 设置注册追踪 Cookie
 */
export function setRegisterTrackingCookies(): { imRef: string | null; irpid: string | null } {
  const urlParams = sessionStorage.getItem('data_url_params');
  const currentSearch = typeof window !== 'undefined' ? window.location.search : '';
  const { imRef, irpid } = getRegisterTrackingParams(currentSearch, urlParams);
  const cookiePath = `${USER_API_BASE_URL}/accounts`;

  if (imRef) {
    document.cookie = `im_ref=${imRef}; path=${cookiePath}`;
  }
  if (irpid) {
    document.cookie = `irpid=${irpid}; path=${cookiePath}`;
  }

  return { imRef, irpid };
}

/**
 * 清理注册追踪 Cookie
 */
export function clearRegisterTrackingCookies(imRef: string | null, irpid: string | null): void {
  const cookiePath = `${USER_API_BASE_URL}/accounts`;

  if (imRef) {
    document.cookie = `im_ref=; path=${cookiePath}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
  if (irpid) {
    document.cookie = `irpid=; path=${cookiePath}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
}

/**
 * 邮箱注册
 */
export async function registerByEmail(data: RegisterParams, config?: WegicRequestConfig) {
  const { imRef, irpid } = setRegisterTrackingCookies();

  try {
    const result = await userRequest.post<RegisterRes>(
      '/accounts/email',
      {
        ...data,
        password: encryptPassword(data.password),
      },
      config,
    );

    return result;
  } finally {
    clearRegisterTrackingCookies(imRef, irpid);
  }
}

/**
 * 邮箱登录
 */
export function loginByEmail(data: LoginParams, config?: WegicRequestConfig) {
  return userRequest.post<LoginRes>(
    '/accounts/email/login',
    {
      ...data,
      password: encryptPassword(data.password),
    },
    config,
  );
}

/**
 * 重置密码
 */
export function resetPassword(data: ResetParams, config?: WegicRequestConfig) {
  return userRequest.patch<User>(
    '/password',
    {
      ...data,
      type: 'email',
      password: encryptPassword(data.password),
    },
    config,
  );
}

/**
 * 发送邮箱验证码
 */
export function sendEmailVerifyCode(email: string, usage: 'register' | 'reset', config?: WegicRequestConfig) {
  return userRequest.post('/accounts/verification-codes/email', { usage, email }, config);
}

/**
 * 获取当前登录用户信息
 */
export function getUserInfo(config?: WegicRequestConfig) {
  return userRequest.get<User>('/accounts', config);
}

/**
 * 获取邀请码信息
 */
export function getInviteCodeInfo(inviteCode: string, config?: WegicRequestConfig) {
  return userRequest.get<InviteCodeRes>(`/inviteCode/${inviteCode}`, config);
}

/**
 * 检查 IP 是否被封禁
 */
export function getIpIsBlock(config?: WegicRequestConfig) {
  return userRequest.post<{ freeRightBlock: boolean }>('/right/check', {}, config);
}
